import React, { useMemo } from "react";
import { formatTime, formatWorkTime } from "../../salaries/salariesListGrid";
import {
    LinkToEmployeeWorkTimeDetails,
    ReportingDetailsTable,
} from "../components/reportingDetailsTable";
import { StrMeasureUnit } from "../../../models/common/enums";
import { SalaryRowTypeItem } from "../../../models/salary/salaryRowTypeItem";
import { WorkTimeReportGridRow } from "../../../models/reporting/workTimeReport";
import { Base } from "../../../framework/base";
import { HourBookingCategory } from "../../../models/workTime/hourBookingCategory";
import { useTranslation } from "react-i18next";
import { GroupedRow } from "../../framework/groupedDataGrid/groupableDataGrid";

interface WorkTimeDetailsProps {
    row: GroupedRow<WorkTimeReportGridRow>;
    salaryRowTypes: SalaryRowTypeItem[];
}

interface WorkTimeDetailsTableRow {
    employeeId: string;
    employeeName: string;
    costCenterName: string | null;
    customerName: string | null;
    vehicleName: string | null;
    workTimeTypeName: string | null;
    dateStr: string;
    comment: string | null;
    startTime: string | null;
    endTime: string | null;
    salaryRowTypeId: string | null;
    category: HourBookingCategory;
    amount: number;
    rowId: string;
}

export const WorkTimeDetailsTable = ({
    row,
    salaryRowTypes,
}: WorkTimeDetailsProps) => {
    const { t } = useTranslation();
    const { rows } = row;

    const tableRows: WorkTimeDetailsTableRow[] = useMemo(
        () =>
            rows?.flatMap(
                (
                    {
                        employeeId,
                        employeeName,
                        costCenterName,
                        workTimeTypeName,
                        customerName,
                        vehicleName,
                        dateStr,
                        comment,
                        salaryRows,
                    },
                    i
                ) =>
                    salaryRows.map(
                        (
                            {
                                salaryRowTypeId,
                                amount,
                                startTime,
                                endTime,
                                category,
                            },
                            j
                        ) => ({
                            employeeId,
                            employeeName,
                            costCenterName,
                            workTimeTypeName,
                            customerName,
                            vehicleName,
                            dateStr,
                            comment,
                            startTime,
                            endTime,
                            salaryRowTypeId,
                            category,
                            amount,
                            rowId: `${i}.${j}`,
                        })
                    )
            ) ?? [],
        [rows]
    );

    const columns = useMemo(
        () => [
            {
                header: t("reporting.date"),
                renderCell: (row: WorkTimeDetailsTableRow) => (
                    <LinkToEmployeeWorkTimeDetails
                        employeeId={row.employeeId}
                        dateStr={row.dateStr}
                        content={Base.dayjsToDateStrWithWeekday(row.dateStr)}
                    />
                ),
            },
            {
                header: t("vehicle.startTime"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    !!row.startTime
                        ? formatTime(row.startTime, row.dateStr)
                        : "-",
            },
            {
                header: t("vehicle.endTime"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    !!row.endTime ? formatTime(row.endTime, row.dateStr) : "-",
            },
            {
                header: t("vehicle.employee"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.employeeName ?? "-",
            },
            {
                header: t("vehicle.amount"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    formatWorkTime(
                        row?.amount,
                        salaryRowTypes?.find(
                            (t) => t.id === row.salaryRowTypeId
                        )?.measureUnit ?? StrMeasureUnit.HourFi,
                        2
                    ),
            },
            {
                header: t("vehicle.salaryRowType"),
                renderCell: (row: WorkTimeDetailsTableRow) => {
                    const srt = salaryRowTypes?.find(
                        (srt) => srt.id === row?.salaryRowTypeId
                    );
                    return srt
                        ? `${srt.code} ${srt.name} (${srt.measureUnit})`
                        : t(`hourBookingCategory.${row.category}`);
                },
                colSpan: 2,
            },
            {
                header: t("reporting.fleet"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.vehicleName ?? "-",
            },
            {
                header: t("reporting.customer"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.customerName ?? "-",
            },
            {
                header: t("reporting.costCenter"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.costCenterName ?? "-",
            },
            {
                header: t("workTime.workTimeType"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.workTimeTypeName ?? "-",
            },
            {
                header: t("vehicle.comment"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.comment || "-",
            },
        ],
        [salaryRowTypes, t]
    );

    return <ReportingDetailsTable columns={columns} rows={tableRows} />;
};
